/**
 * AudioBus — pure channel-state machine for MANOR's audio graph.
 *
 * No Phaser, no Howler import — fully testable in Node/Vitest.
 * AudioManager (Phaser-side) reads effective volumes from the bus; music
 * playback uses the HowlLike interface so Howler can be swapped in later.
 *
 * TODO(MY-1429): Install Howler when music assets are ready:
 *   pnpm add howler @types/howler
 * Then replace NoopHowl with a real HowlFactory backed by `new Howl(...)`.
 */

export type Channel = 'master' | 'music' | 'sfx';

/** Slim interface matching the Howler API surface MANOR uses. */
export interface HowlLike {
  play(): void;
  stop(): void;
  volume(v: number): void;
  loop(l: boolean): void;
}

/** Drop-in no-op stub — silent until real Howler assets are wired. */
export class NoopHowl implements HowlLike {
  play(): void {}
  stop(): void {}
  volume(_v: number): void {}
  loop(_l: boolean): void {}
}

export const DEFAULT_MASTER_VOLUME = 1.0;
export const DEFAULT_MUSIC_VOLUME = 0.8;
export const DEFAULT_SFX_VOLUME = 1.0;
/** Music ducks to this fraction of its normal volume while SFX is flagged. */
export const DEFAULT_DUCK_FACTOR = 0.4;

/** Subset of GameOptions that AudioBus reads and writes. */
export interface AudioBusOptions {
  musicVolume: number;
  sfxVolume: number;
  /** Optional: absent in old saves, defaults to DEFAULT_MASTER_VOLUME. */
  masterVolume?: number;
  /** Optional: absent in old saves, defaults to false. */
  masterMuted?: boolean;
}

export class AudioBus {
  private masterVolume: number;
  private musicVolume: number;
  private sfxVolume: number;

  private masterMuted = false;
  private musicMuted = false;
  private sfxMuted = false;

  private readonly duckFactor: number;
  private _isDucking = false;

  constructor(duckFactor = DEFAULT_DUCK_FACTOR) {
    this.duckFactor = duckFactor;
    this.masterVolume = DEFAULT_MASTER_VOLUME;
    this.musicVolume = DEFAULT_MUSIC_VOLUME;
    this.sfxVolume = DEFAULT_SFX_VOLUME;
  }

  setVolume(channel: Channel, volume: number): void {
    const v = Math.max(0, Math.min(1, volume));
    if (channel === 'master') this.masterVolume = v;
    else if (channel === 'music') this.musicVolume = v;
    else this.sfxVolume = v;
  }

  getVolume(channel: Channel): number {
    if (channel === 'master') return this.masterVolume;
    if (channel === 'music') return this.musicVolume;
    return this.sfxVolume;
  }

  setMute(channel: Channel, muted: boolean): void {
    if (channel === 'master') this.masterMuted = muted;
    else if (channel === 'music') this.musicMuted = muted;
    else this.sfxMuted = muted;
  }

  isMuted(channel: Channel): boolean {
    if (channel === 'master') return this.masterMuted;
    if (channel === 'music') return this.musicMuted;
    return this.sfxMuted;
  }

  /**
   * Effective output volume for a channel.
   * Accounts for: master mute → channel mute → duck (music only) → master scale.
   */
  getEffectiveVolume(channel: Channel): number {
    if (this.masterMuted) return 0;
    if (channel === 'master') return this.masterVolume;

    const channelMuted = channel === 'music' ? this.musicMuted : this.sfxMuted;
    if (channelMuted) return 0;

    const channelVol = channel === 'music' ? this.musicVolume : this.sfxVolume;
    const duck = channel === 'music' && this._isDucking ? this.duckFactor : 1.0;

    return channelVol * duck * this.masterVolume;
  }

  startDuck(): void {
    this._isDucking = true;
  }

  stopDuck(): void {
    this._isDucking = false;
  }

  isDucking(): boolean {
    return this._isDucking;
  }

  /** Load volumes from a GameOptions-shaped object. */
  fromOptions(opts: AudioBusOptions): void {
    if (opts.masterVolume !== undefined) this.setVolume('master', opts.masterVolume);
    this.setVolume('music', opts.musicVolume);
    this.setVolume('sfx', opts.sfxVolume);
    if (opts.masterMuted !== undefined) this.setMute('master', opts.masterMuted);
  }

  /** Return the serialisable subset for merging into GameOptions. */
  toOptions(): AudioBusOptions {
    return {
      masterVolume: this.masterVolume,
      musicVolume: this.musicVolume,
      sfxVolume: this.sfxVolume,
      masterMuted: this.masterMuted,
    };
  }
}

// ── Production singleton ─────────────────────────────────────────────────────

let _bus: AudioBus | null = null;

/** Shared AudioBus instance used by AudioManager, MusicDirectorAdapter, and PauseScene. */
export function getAudioBus(): AudioBus {
  if (!_bus) _bus = new AudioBus();
  return _bus;
}
